import type { ReactNode } from 'react';

import { focusRing } from '@/design-system';
import type { PublicLanguage, PublicNhgAnnex, PublicNhgLine } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { formatAmount } from '../format';
import { isModifiedClick } from '../jump';
import { entryTitle, nhgRowAnchor, type FullReportIndex, type IndexedEntry } from '../reportIndex';
import { chapterAnchor } from '../useReportView';
import type { HeadingLevel } from './ReportChapter';

export interface NhgAnnexProps {
    annex: PublicNhgAnnex;
    /** Het niveau van de kop van het tabblad; de delen van de bijlage staan eronder. */
    level: 1 | 2;
    language: PublicLanguage;
    copy: PublicCopy;
    /** Welke bevindingen achter elke regel staan. */
    index: FullReportIndex;
    /** Van een regel naar een bevinding in het rapport. */
    onEntryJump: (indexed: IndexedEntry) => void;
}

/*
 * Breed zijn de tabellen van de bijlage tabellen, zoals op het formulier; smal
 * (onder 600 px paginabreedte) wordt elke regel een blok met de namen van de
 * kolommen erbij, zodat niets zijwaarts hoeft te schuiven. De rollen staan er
 * uitdrukkelijk op: een tabel die smal geen tabel meer tekent, blijft er voor
 * een schermlezer wel een.
 */
const table = 'block w-full border-collapse @min-[600px]:table';
const head = 'sr-only @min-[600px]:not-sr-only @min-[600px]:table-header-group';
const body = 'block @min-[600px]:table-row-group';
const foot = 'block @min-[600px]:table-footer-group';
const cellWide = '@min-[600px]:table-cell @min-[600px]:px-2 @min-[600px]:py-[9px]';
/** De naam van de kolom naast een waarde, alleen smal; breed staat hij in de kop. */
const inlineLabel = 'text-[var(--text-tertiary-safe)] @min-[600px]:hidden';

/**
 * Het Model bouwkundig rapport NHG, zoals in de PDF: model, toelichting,
 * verzamelstaat, de weg van de rapportkosten naar de bijlage, opmerkingen, de
 * resultatentabellen A en B met elke regel van het model, de legenda en het
 * programma van eisen. De zinnen komen van de API (`NhgAnnexText`), dus precies
 * die van de PDF. Het formulier voor de geldverstrekker (aanvrager, handtekening)
 * staat alleen in de PDF, en dat zegt de pagina.
 *
 * Elke regel van de resultatentabellen heeft een anker, waar de NHG-code onder
 * een bevinding naartoe springt; de regel zet de bevindingen erachter op een
 * rij, elk met een link naar het rapport.
 */
export function NhgAnnex({ annex, level, language, copy, index, onEntryJump }: NhgAnnexProps) {
    const sub = (level + 1) as HeadingLevel;
    const money = (amount: number) => formatAmount(amount, language);
    /* Een lege regel van het model is een streepje, zoals op het formulier. */
    const cell = (amount: number) => (amount === 0 ? '-' : money(amount));
    const [directColumn = '', termColumn = '', totalColumn = ''] = annex.summary.columns;

    return (
        <div className="flex flex-col gap-4">
            {annex.model ? (
                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)] italic">
                    {annex.model}
                </p>
            ) : null}

            <p className="m-0 text-[15px] leading-[1.6] text-pretty text-[var(--text-reading)]">
                {annex.intro}
            </p>

            <p className="m-0 rounded-r-[8px] border-l-2 border-[var(--accent)] bg-[var(--surface-subtle)] px-4 py-3 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                {copy.nhgAdministration}
            </p>

            <Part level={sub} title={annex.summary.title}>
                <Frame>
                    <table
                        role="table"
                        aria-label={annex.summary.title}
                        className={cn(table, '@min-[600px]:min-w-[440px]')}
                    >
                        <thead role="rowgroup" className={cn(head, 'bg-[var(--surface-subtle)]')}>
                            <tr role="row" className="@min-[600px]:table-row">
                                <td role="cell" />
                                {annex.summary.columns.map((column) => (
                                    <HeaderCell key={column} align="end">
                                        {column}
                                    </HeaderCell>
                                ))}
                            </tr>
                        </thead>
                        <tbody role="rowgroup" className={body}>
                            {annex.summary.rows.map((row) => (
                                <SummaryRow
                                    key={row.label}
                                    line={row}
                                    columns={[directColumn, termColumn, totalColumn]}
                                    money={money}
                                />
                            ))}
                        </tbody>
                        <tfoot role="rowgroup" className={foot}>
                            <SummaryRow
                                line={annex.summary.total}
                                columns={[directColumn, termColumn, totalColumn]}
                                money={money}
                                emphasis
                            />
                            <tr
                                role="row"
                                className="flex items-baseline justify-between gap-3 border-t border-[var(--border-subtle-2)] px-3 py-[10px] @min-[600px]:table-row @min-[600px]:p-0"
                            >
                                <th
                                    role="rowheader"
                                    scope="row"
                                    colSpan={3}
                                    className={cn(
                                        'block text-left text-[13px] leading-[1.4] font-normal text-[var(--text-primary)]',
                                        cellWide,
                                    )}
                                >
                                    {annex.summary.improvement.label}
                                </th>
                                <AmountCell>{money(annex.summary.improvement.amount)}</AmountCell>
                            </tr>
                        </tfoot>
                    </table>
                </Frame>
            </Part>

            <Part level={sub} title={annex.reconciliation.title}>
                {/* Twee kolommen passen ook op een telefoon: de tekst loopt door, het bedrag blijft rechts. */}
                <Frame>
                    <table
                        aria-label={annex.reconciliation.title}
                        className="w-full border-collapse"
                    >
                        <tbody>
                            {annex.reconciliation.rows.map((row) => (
                                <tr
                                    key={row.label}
                                    className="border-t border-[var(--border-subtle-2)] first:border-t-0"
                                >
                                    <th
                                        scope="row"
                                        className="px-3 py-[9px] text-left text-[13px] leading-[1.4] font-normal text-pretty text-[var(--text-primary)] @min-[600px]:px-2"
                                    >
                                        {row.label}
                                    </th>
                                    <td className="px-3 py-[9px] text-right align-top font-[family-name:var(--font-mono)] text-[13px] whitespace-nowrap text-[var(--text-primary)] @min-[600px]:px-2">
                                        {money(row.amount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Frame>
                <p className="m-0 mt-3 text-[13px] leading-[1.5] text-pretty text-[var(--text-tertiary-safe)] italic">
                    {annex.reconciliation.note}
                </p>
            </Part>

            <Part level={sub} title={annex.remarks.title}>
                <Bullets items={annex.remarks.items} />
            </Part>

            {annex.results.map((results) => {
                const [, , , actionColumn = '', shareColumn = '', direct = '', term = ''] =
                    results.columns;

                return (
                    <Part key={results.title} level={sub} title={results.title}>
                        {results.note ? (
                            <p className="m-0 mb-3 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)] italic">
                                {results.note}
                            </p>
                        ) : null}
                        <Frame>
                            <table
                                role="table"
                                aria-label={results.title}
                                className={cn(table, '@min-[600px]:min-w-[560px]')}
                            >
                                <thead
                                    role="rowgroup"
                                    className={cn(head, 'bg-[var(--surface-subtle)]')}
                                >
                                    <tr role="row" className="@min-[600px]:table-row">
                                        {results.columns.map((column, position) => (
                                            <HeaderCell
                                                key={column}
                                                align={position >= 5 ? 'end' : 'start'}
                                            >
                                                {column}
                                            </HeaderCell>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody role="rowgroup" className={body}>
                                    {results.rows.map((row) => {
                                        const findings = index.byNhgCode.get(row.code) ?? [];
                                        const located =
                                            findings.length > 0 || row.locations.length > 0;

                                        return (
                                            <tr
                                                key={row.code}
                                                role="row"
                                                id={nhgRowAnchor(row.code)}
                                                tabIndex={-1}
                                                className={cn(
                                                    'flex scroll-mt-[64px] flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-[var(--border-subtle-2)] px-3 py-[10px] outline-none',
                                                    'focus:bg-[var(--accent-tint)] data-[jumped]:bg-[var(--accent-tint)]',
                                                    '@min-[600px]:table-row @min-[600px]:p-0',
                                                )}
                                            >
                                                <td
                                                    role="cell"
                                                    className={cn(
                                                        'shrink-0 align-top font-[family-name:var(--font-mono)] text-[12px] whitespace-nowrap text-[var(--text-secondary)]',
                                                        cellWide,
                                                    )}
                                                >
                                                    {row.code}
                                                </td>
                                                <td
                                                    role="cell"
                                                    className={cn(
                                                        'min-w-0 flex-1 align-top text-[13px] leading-[1.4] font-medium text-[var(--text-primary)] @min-[600px]:font-normal',
                                                        cellWide,
                                                    )}
                                                >
                                                    {row.element}
                                                </td>
                                                <td
                                                    role="cell"
                                                    className={cn(
                                                        'basis-full align-top text-[13px] leading-[1.4] text-[var(--text-secondary)]',
                                                        !located && 'hidden',
                                                        cellWide,
                                                    )}
                                                >
                                                    <RowFindings
                                                        locations={row.locations}
                                                        findings={findings}
                                                        onEntryJump={onEntryJump}
                                                    />
                                                </td>
                                                <MetaCell
                                                    label={actionColumn}
                                                    empty={row.action === ''}
                                                >
                                                    {row.action}
                                                </MetaCell>
                                                <MetaCell
                                                    label={shareColumn}
                                                    empty={row.share === ''}
                                                >
                                                    {row.share}
                                                </MetaCell>
                                                <MetaCell
                                                    label={direct}
                                                    empty={row.direct === 0}
                                                    amount
                                                >
                                                    {cell(row.direct)}
                                                </MetaCell>
                                                <MetaCell
                                                    label={term}
                                                    empty={row.termijn === 0}
                                                    amount
                                                >
                                                    {cell(row.termijn)}
                                                </MetaCell>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot role="rowgroup" className={foot}>
                                    <tr
                                        role="row"
                                        className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-[var(--border-default)] bg-[var(--surface-subtle)] px-3 py-[10px] @min-[600px]:table-row @min-[600px]:p-0"
                                    >
                                        <th
                                            role="rowheader"
                                            scope="row"
                                            colSpan={5}
                                            className={cn(
                                                'basis-full text-left text-[13px] font-medium text-[var(--text-primary)]',
                                                cellWide,
                                            )}
                                        >
                                            {results.total.label}
                                        </th>
                                        <MetaCell label={direct} amount emphasis>
                                            {money(results.total.direct)}
                                        </MetaCell>
                                        <MetaCell label={term} amount emphasis>
                                            {money(results.total.termijn)}
                                        </MetaCell>
                                    </tr>
                                </tfoot>
                            </table>
                        </Frame>
                    </Part>
                );
            })}

            <Part level={sub} title={annex.legend.title}>
                <Bullets items={annex.legend.items} />
            </Part>

            <Part level={sub} title={annex.programme.title}>
                {/* Per code een blok in plaats van een derde kolom: zo past het programma ook op een telefoon. */}
                <ul className="m-0 flex list-none flex-col divide-y divide-[var(--border-subtle-2)] rounded-[10px] border border-[var(--border-default)] p-0">
                    {annex.programme.rows.map((row) => (
                        <li key={row.code} className="m-0 flex gap-3 px-3 py-3">
                            <span className="w-11 shrink-0 font-[family-name:var(--font-mono)] text-[12px] leading-[1.5] text-[var(--text-secondary)]">
                                {row.code}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="m-0 text-[13px] leading-[1.45] font-medium text-[var(--text-primary)]">
                                    {row.title}
                                </p>
                                {row.items.map((item) => (
                                    <p
                                        key={item}
                                        className="m-0 mt-1 text-[13px] leading-[1.5] text-pretty text-[var(--text-secondary)]"
                                    >
                                        {item}
                                    </p>
                                ))}
                                {row.bbl.length > 0 ? (
                                    <p className="m-0 mt-2 text-[12px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                                        <span className="font-medium">
                                            {annex.programme.columns[2]}
                                        </span>
                                        {': '}
                                        {row.bbl.join(' · ')}
                                    </p>
                                ) : null}
                            </div>
                        </li>
                    ))}
                </ul>
            </Part>
        </div>
    );
}

/**
 * De bevindingen achter een regel van de bijlage: elk met de plek en de titel,
 * als link naar de bevinding in het rapport. De kolom heet "Locatie en
 * omschrijving gebrek", en dat staat er dan ook. Een plek zonder bevinding op
 * de pagina blijft gewone tekst, zoals in de PDF.
 */
function RowFindings({
    locations,
    findings,
    onEntryJump,
}: {
    locations: string[];
    findings: IndexedEntry[];
    onEntryJump: (indexed: IndexedEntry) => void;
}) {
    const unlinked = locations.filter(
        (location) => !findings.some((indexed) => indexed.placeNumber === location),
    );

    if (findings.length === 0) {
        return <>{locations.join(', ')}</>;
    }

    return (
        <ul className="m-0 flex list-none flex-col p-0">
            {findings.map((indexed) => (
                <li key={indexed.anchor} className="m-0">
                    <a
                        href={`#${chapterAnchor(indexed.targetKey)}`}
                        onClick={(event) => {
                            if (isModifiedClick(event)) {
                                return;
                            }

                            event.preventDefault();
                            onEntryJump(indexed);
                        }}
                        className={cn(
                            '-ml-1 inline-flex min-h-[44px] items-center gap-[6px] rounded-[6px] px-1 text-left',
                            'text-[13px] leading-[1.35] text-[var(--accent-dark)] underline decoration-[var(--accent-border)] underline-offset-2',
                            'hover:text-[var(--accent)] hover:decoration-[var(--accent)]',
                            focusRing,
                        )}
                    >
                        {indexed.placeNumber ? (
                            <>
                                <span className="font-[family-name:var(--font-mono)] text-[12px] text-[var(--text-secondary)] no-underline">
                                    {indexed.placeNumber}
                                </span>{' '}
                            </>
                        ) : null}
                        {entryTitle(indexed.entry)}
                    </a>
                </li>
            ))}
            {unlinked.length > 0 ? <li className="m-0 px-1 py-2">{unlinked.join(', ')}</li> : null}
        </ul>
    );
}

function Part({
    level,
    title,
    children,
}: {
    level: HeadingLevel;
    title: string;
    children: ReactNode;
}) {
    const Heading = `h${level}` as const;

    return (
        <div className="mt-2">
            <Heading className="m-0 mb-3 font-[family-name:var(--font-display)] text-[16px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                {title}
            </Heading>
            {children}
        </div>
    );
}

/**
 * Het kader om een tabel. Breed schuift een tabel die te breed is binnen zijn
 * kader, met een minimale breedte; hij wordt nooit afgekapt. Smal is hij een
 * lijst en past hij altijd.
 */
function Frame({ children }: { children: ReactNode }) {
    return (
        <div className="rounded-[10px] border border-[var(--border-default)] @min-[600px]:overflow-x-auto">
            {children}
        </div>
    );
}

function HeaderCell({ align, children }: { align: 'start' | 'end'; children: ReactNode }) {
    return (
        <th
            role="columnheader"
            scope="col"
            className={cn(
                'px-2 py-[9px] align-bottom text-[12px] leading-[1.35] font-medium text-[var(--text-tertiary-safe)]',
                align === 'end' ? 'text-right' : 'text-left',
            )}
        >
            {children}
        </th>
    );
}

function AmountCell({ emphasis = false, children }: { emphasis?: boolean; children: ReactNode }) {
    return (
        <td
            role="cell"
            className={cn(
                'block text-right align-top font-[family-name:var(--font-mono)] text-[13px] whitespace-nowrap text-[var(--text-primary)]',
                cellWide,
                emphasis && 'font-medium',
            )}
        >
            {children}
        </td>
    );
}

/**
 * Een cel met de naam van zijn kolom ervoor, zolang de tabel smal een lijst is.
 * Smal valt een lege cel weg (geen actie, geen bedrag); breed staat hij er, met
 * zijn streepje, zoals op het formulier.
 */
function MetaCell({
    label,
    empty = false,
    amount = false,
    emphasis = false,
    children,
}: {
    label: string;
    empty?: boolean;
    amount?: boolean;
    emphasis?: boolean;
    children: ReactNode;
}) {
    return (
        <td
            role="cell"
            className={cn(
                'align-top text-[13px] leading-[1.4] whitespace-nowrap text-[var(--text-secondary)]',
                empty ? 'hidden' : 'inline-flex gap-[6px]',
                amount &&
                    'font-[family-name:var(--font-mono)] text-[var(--text-primary)] @min-[600px]:text-right',
                emphasis && 'font-medium',
                cellWide,
            )}
        >
            <span
                aria-hidden="true"
                className={cn(inlineLabel, 'font-[family-name:var(--font-body)] font-normal')}
            >
                {label}
            </span>
            <span>{children}</span>
        </td>
    );
}

function SummaryRow({
    line,
    columns,
    money,
    emphasis = false,
}: {
    line: PublicNhgLine;
    columns: [string, string, string];
    money: (amount: number) => string;
    emphasis?: boolean;
}) {
    const amounts = [line.direct, line.termijn, line.total];

    return (
        <tr
            role="row"
            className={cn(
                'flex flex-col gap-1 border-t px-3 py-[10px] @min-[600px]:table-row @min-[600px]:p-0',
                emphasis
                    ? 'border-[var(--border-default)] bg-[var(--surface-subtle)]'
                    : 'border-[var(--border-subtle-2)]',
            )}
        >
            <th
                role="rowheader"
                scope="row"
                className={cn(
                    'block text-left text-[13px] leading-[1.4] text-[var(--text-primary)]',
                    emphasis ? 'font-medium' : 'font-medium @min-[600px]:font-normal',
                    cellWide,
                )}
            >
                {line.label}
            </th>
            {amounts.map((value, position) => (
                <td
                    key={position}
                    role="cell"
                    className={cn(
                        'flex items-baseline justify-between gap-3 text-[13px] leading-[1.4] text-[var(--text-primary)] @min-[600px]:text-right',
                        cellWide,
                        emphasis && 'font-medium',
                    )}
                >
                    <span aria-hidden="true" className={cn(inlineLabel, 'text-pretty')}>
                        {columns[position]}
                    </span>
                    <span className="font-[family-name:var(--font-mono)] whitespace-nowrap">
                        {money(value)}
                    </span>
                </td>
            ))}
        </tr>
    );
}

function Bullets({ items }: { items: string[] }) {
    return (
        <ul className="m-0 flex list-disc flex-col gap-1 pl-5">
            {items.map((item) => (
                <li
                    key={item}
                    className="pl-[2px] text-[14px] leading-[1.55] text-pretty text-[var(--text-reading)] marker:text-[var(--text-tertiary-safe)]"
                >
                    {item}
                </li>
            ))}
        </ul>
    );
}
